import { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuthUser } from '@/hooks/useAuthUser';
import type { UserDoc, WithId } from '@/types/firestore';
import { Loader2, ShieldAlert } from 'lucide-react';
import { FORCE_ADMIN_MODE } from '@/config/devOverrides';

interface RequireAdminAccessProps {
  children: React.ReactNode;
}

export default function RequireAdminAccess({ children }: RequireAdminAccessProps) {
  const { status, firebaseUser, userDoc, loading: authLoading } = useAuthUser();
  const [claimsState, setClaimsState] = useState<{ loading: boolean; role: 'super' | 'support' | null }>({
    loading: true, // Start with true to prevent premature access while claims are fetched
    role: null,
  });
  const [hasTimedOut, setHasTimedOut] = useState(false);

  useEffect(() => {
    if (!firebaseUser) {
      setClaimsState({ loading: false, role: null });
      setHasTimedOut(false);
      return;
    }

    let active = true;
    setHasTimedOut(false);
    setClaimsState({ loading: true, role: null });

    const timeoutId = window.setTimeout(() => {
      if (active) {
        console.error('[RequireAdminAccess] Token refresh timed out after 5s');
        setHasTimedOut(true);
        setClaimsState({ loading: false, role: null });
      }
    }, 5_000);

    firebaseUser
      .getIdTokenResult(true)
      .then((result) => {
        if (!active) {
          return;
        }

        const claims = result.claims ?? {};
        const roleClaim = typeof claims.role === 'string' ? claims.role : undefined;
        const normalizedRole = roleClaim === 'super' ? 'super' : roleClaim === 'support' ? 'support' : null;

        // [P2 Admin Support] E2E diagnostic - expose what guard actually saw
        if (typeof window !== 'undefined' && (window as any).__TL_E2E__) {
          (window as any).__TL_E2E__.lastAdminClaimsSeen = {
            admin: claims.admin,
            role: normalizedRole,
            rawRole: claims.role,
            allClaims: claims,
            timestamp: Date.now(),
          };
        }

        if (import.meta.env.DEV) {
          console.info('[RequireAdminAccess] Admin claim check:', {
            email: firebaseUser.email,
            role: normalizedRole,
            claims,
          });
        }

        setClaimsState({ loading: false, role: normalizedRole });
        window.clearTimeout(timeoutId);
      })
      .catch((error) => {
        console.error('Error checking admin claim:', error);
        if (active) {
          setClaimsState({ loading: false, role: null });
        }
      })
      .finally(() => {
        if (!active) {
          return;
        }
        window.clearTimeout(timeoutId);
      });

    return () => {
      active = false;
      window.clearTimeout(timeoutId);
    };
  }, [firebaseUser]);

  const roleFromDoc = useMemo(() => {
    const doc = userDoc as WithId<UserDoc> | null;
    if (!doc) {
      return null;
    }

    const roles = doc.roles ?? {};
    const flags = roles as Record<string, unknown>;
    const hasFlag = (key: string) => flags[key] === true;

    const flagRole = typeof flags['role'] === 'string'
      ? (flags['role'] as string).toLowerCase()
      : undefined;

    if (doc.isAdmin === true || hasFlag('isAdmin') || hasFlag('isSuperAdmin') || hasFlag('admin') || hasFlag('super') || flagRole === 'super') {
      return 'super' as const;
    }

    if (hasFlag('isSupportAdmin') || hasFlag('support') || flagRole === 'support') {
      return 'support' as const;
    }

    return null;
  }, [userDoc]);

  const isAuthenticated = status === 'authed' && !!firebaseUser;
  const combinedLoading = authLoading || claimsState.loading || status === 'loading';
  const hasAdminAccess = roleFromDoc !== null || claimsState.role !== null || FORCE_ADMIN_MODE;

  // Still loading/initializing or checking admin status
  if (combinedLoading) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-slate-950">
        <div className="text-center">
          <Loader2 className="mx-auto h-12 w-12 animate-spin text-orange-500" />
          <p className="mt-4 text-slate-400">Verifying admin access...</p>
        </div>
      </main>
    );
  }

  // Not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Not an admin or timed out
  if (!hasAdminAccess || hasTimedOut) {
    return (
      <main data-testid="access-denied" className="flex min-h-screen items-center justify-center bg-slate-950 px-4">
        <div className="max-w-md rounded-lg border-2 border-red-500/30 bg-red-900/20 p-8 text-center">
          <ShieldAlert className="mx-auto h-16 w-16 text-red-400" />
          <h1 className="mt-4 text-2xl font-bold text-red-200">Access Denied</h1>
          <p className="mt-2 text-red-200/80">
            {hasTimedOut 
              ? "Admin verification timed out. Please try again or contact support."
              : "You do not have admin privileges. If you believe this is an error, please contact support."
            }
          </p>
          <button
            onClick={() => window.history.back()}
            className="mt-6 rounded-lg bg-slate-700 px-6 py-2 font-medium text-slate-200 transition-colors hover:bg-slate-600"
          >
            Go Back
          </button>
        </div>
      </main>
    );
  }

  // User is admin
  return <>{children}</>;
}
